import { useEffect, useMemo, useState } from 'react';
import { ClipboardCheck } from 'lucide-react';
import { ApprovingForSwitcher, ApprovingForBanner, MOCK_PERMITTED_APPROVERS } from './ApprovingForSwitcher';
import type { ApprovingForSelection } from './ApprovingForSwitcher';
import { MOCK_APPROVER_ROWS } from './mockApprovingForData';
import { toast } from 'sonner';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { useTour } from '@/contexts/useTour';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useOrganisationSettings } from '@/hooks/useOrganisationSettings';
import { useOvertimeTypes } from '@/hooks/useOvertimeTypes';
import { useApprovalAutomation } from '@/hooks/use-approval-automation';
import { AuditHistoryDialog } from '@/components/AuditHistory';
import { BookingDetailsModal } from '@/components/ScheduledBookings';
import { PreStageRuleConfirmationDialog } from '@/components/PreStageRuleConfirmationDialog';
import { RejectTimesheetDialog } from '@/components/approvals/RejectTimesheetDialog';
import { BulkRejectDialog } from '@/components/approvals/BulkRejectDialog';
import { PageHeader } from '@/components/blocks/page-header';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import type { ApprovalRow } from '@/components/ApprovalGrid';
import type { DayKey, Timesheet } from '@/types/timesheet';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import { getEffectiveDescriptionType } from '@/utils/descriptionTypeUtils';
import { buildDaysFromStart } from '@/utils/ApprovalDashboard/dayHelpers';
import { getLivePhotoUrl, getPhotoUrl } from '@/utils/ApprovalDashboard/photoUrl';
import { calculateTimesheetTotal } from '@/utils/ApprovalDashboard/timesheetTotals';
import { useApprovalQueueData } from '@/hooks/ApprovalDashboard/useApprovalQueueData';
import { useProjectCostCentres } from '@/hooks/ApprovalDashboard/useProjectCostCentres';
import { useApprovalAnomalies } from '@/hooks/ApprovalDashboard/useApprovalAnomalies';
import { useApprovalFilters } from '@/hooks/ApprovalDashboard/useApprovalFilters';
import { useApprovalSelection } from '@/hooks/ApprovalDashboard/useApprovalSelection';
import { useApprovalActions } from '@/hooks/ApprovalDashboard/useApprovalActions';
import { useTimesheetReviewEdit } from '@/hooks/ApprovalDashboard/useTimesheetReviewEdit';
import { ApprovalKpiCards } from './ApprovalKpiCards';
import { ApprovalQueueCard } from './ApprovalQueueCard';
import { BulkApprovalDialog } from './BulkApprovalDialog';
import { FlaggedApprovalDialog } from './FlaggedApprovalDialog';
import { IssuesSheet } from './IssuesSheet';
import { TimesheetReviewDialog } from './TimesheetReviewDialog';
import type { BookingDetails } from './TimesheetReviewRow';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';

export function ApprovalDashboard() {
  const { timesheets, employees, projects, currentEmployee, updateTimesheet } = useTimesheetForScreen('approvals');
  const { getScreenMode } = useDataSource();
  const { canApprove, canReject, canBulkApprove, canEditTimesheetInApproval, requiresRejectionReason, canViewPolicyViolations, canViewDetectedIssues } = usePermissions();
  const { currentCompany } = useCompany();

  // ── "Approving for" state ──────────────────────────────────────────────────
  const myselfSelection: ApprovingForSelection = { type: 'myself', id: 'myself', name: currentEmployee.name };
  const [approvingFor, setApprovingFor] = useState<ApprovingForSelection>(myselfSelection);
  const { startPendingTour } = useTour();

  // Start any pending tour queued before navigation to this page
  useEffect(() => {
    startPendingTour();
  }, [startPendingTour]);

  const [viewingTimesheet, setViewingTimesheet] = useState<Timesheet | null>(null);
  const [rejectingTimesheet, setRejectingTimesheet] = useState<Timesheet | null>(null);
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [viewingBookingDetails, setViewingBookingDetails] = useState<BookingDetails | null>(null);
  const [showIssuesSheet, setShowIssuesSheet] = useState(false);

  // Week start day from Settings (drives column order, mirrors TimesheetGrid)
  const { weekStartDay, organizationSettings } = useSettings();

  // Get current screen mode
  const screenMode = getScreenMode('approvals');

  // Get organization settings for policy violation calculations
  const { settings: orgSettings, loading: settingsLoading, error: settingsError } = useOrganisationSettings('approvals');
  const orgDescriptionType = (orgSettings?.descriptionType ?? 'row') as 'row' | 'daily';

  // Derive isDaily per-timesheet so that changing the org setting does not
  // retroactively alter how already-submitted timesheets are displayed.
  const isDaily = viewingTimesheet
    ? getEffectiveDescriptionType(orgDescriptionType, viewingTimesheet.status, viewingTimesheet.entries) === 'daily'
    : orgDescriptionType === 'daily';

  // Fetch overtime types from the OvertimeTypes API instead of organizationSettings
  const overtimeEnabledForApprovals = organizationSettings?.overtime?.enabled === true;
  const { activeTypes: apiActiveOvertimeTypes } = useOvertimeTypes({ enabled: overtimeEnabledForApprovals, screenId: 'approvals' });

  // When a timesheet review dialog opens, honour the org setting for whether
  // the issues panel should be expanded by default.
  useEffect(() => {
    if (viewingTimesheet) {
      setShowIssuesSheet(orgSettings.approvalScreen?.panelDefaultExpanded ?? false);
    } else {
      setShowIssuesSheet(false);
    }
  }, [viewingTimesheet, orgSettings.approvalScreen?.panelDefaultExpanded]);

  // Approval Automation
  const automation = useApprovalAutomation(timesheets, employees, currentEmployee.id);

  // Detect patterns on mount and when timesheets change
  useEffect(() => {
    automation.detectPatterns();
  }, [timesheets.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    activeDelegations,
    isLoadingPending,
    pendingTimesheets: allPendingTimesheets,
    approvableFor,
    effectiveProjects,
    selectableProjects,
    projectDataEpoch,
    refreshPendingApprovals,
  } = useApprovalQueueData({ screenMode, timesheets, projects, currentEmployee });

  const showApprovingFor = organizationSettings?.onBehalfOf?.approveOnBehalfOfEnabled ?? false;

  // Live mode: the expanded pending response carries items from other approvers' queues,
  // tagged with queueOwnerId — show only the queue selected in "Approving for".
  const pendingTimesheets = useMemo(() => {
    if (screenMode !== 'live') return allPendingTimesheets;
    return allPendingTimesheets.filter(ts =>
      approvingFor.type === 'other'
        ? ts.queueOwnerId != null && String(ts.queueOwnerId) === approvingFor.id
        : ts.queueOwnerId == null
    );
  }, [screenMode, allPendingTimesheets, approvingFor]);

  const { timesheetAnomalies, timesheetPolicyViolations } = useApprovalAnomalies({
    pendingTimesheets,
    employees,
    timesheets,
    orgSettings,
    screenMode,
    settingsLoading,
    settingsError,
  });

  const {
    departmentFilter,
    setDepartmentFilter,
    anomalyFilter,
    setAnomalyFilter,
    overdueFilter,
    setOverdueFilter,
    filteredTimesheets,
    departments,
    summary,
  } = useApprovalFilters({ pendingTimesheets, employees, timesheetAnomalies, timesheetPolicyViolations });

  const {
    selectedIds,
    selectedRows,
    clearSelection,
    setClearSelection,
    handleSelectionChange,
    selectedTimesheets,
    selectedWithAnomalies,
  } = useApprovalSelection({ filteredTimesheets, timesheetAnomalies });

  const {
    approvingIds,
    rejectingIds,
    isBulkApproving,
    isBulkRejecting,
    isBulkProcessing,
    isRejectingSingle,
    isConfirmingApproval,
    approvalNotes,
    setApprovalNotes,
    bulkApprovalNotes,
    setBulkApprovalNotes,
    showBulkConfirmDialog,
    setShowBulkConfirmDialog,
    showBulkRejectDialog,
    setShowBulkRejectDialog,
    pendingFlaggedApproval,
    setPendingFlaggedApproval,
    postApprovalConfirm,
    approveTimesheet,
    handleApproveClick,
    handleGridApprove,
    handleToolbarBulkApprove,
    handleBulkRejectClick,
    executeBulkApprove,
    executeBulkReject,
    rejectTimesheet,
    handlePostApprovalAccept,
    handlePostApprovalDecline,
    cancelBulkApproval,
  } = useApprovalActions({
    screenMode,
    currentEmployee,
    projects,
    updateTimesheet,
    timesheetAnomalies,
    timesheetPolicyViolations,
    selectedRows,
    setClearSelection,
    refreshPendingApprovals,
    canViewDetectedIssues,
    canViewPolicyViolations,
    requiresRejectionReason,
    rejectingTimesheet,
    setRejectingTimesheet,
    setViewingTimesheet,
  });

  const {
    isEditMode,
    setIsEditMode,
    editedEntries,
    editingHours,
    setEditingHours,
    getHoursCellKey,
    isSavingEdits,
    hasUnsavedChanges,
    hasMissingCostCentres,
    showUnsavedChangesDialog,
    setShowUnsavedChangesDialog,
    expandedEntryIds,
    setExpandedEntryIds,
    toggleEntryExpansion,
    shouldShowExpansionIcon,
    handleEntryFieldChange,
    handleDailyNoteChange,
    handleEntryRoleChange,
    handleEntryHoursChange,
    handleEntryProjectChange,
    handleDialogCloseAttempt,
    handleConfirmDiscard,
    handleCancelEdit,
    handleSaveEdits,
    computedDailyTotals,
  } = useTimesheetReviewEdit({
    viewingTimesheet,
    setViewingTimesheet,
    screenMode,
    currentEmployee,
    updateTimesheet,
    orgSettings,
    companyHasCostingIntegration: currentCompany?.hasCostingIntegration === true,
  });

  const costCentresByProjectCode = useProjectCostCentres({
    screenMode,
    viewingTimesheet,
    editedEntries,
    effectiveProjects,
    projectDataEpoch,
  });

  // In-flight state for the currently viewed timesheet (View Timesheet dialog footer)
  const isViewingApproving = viewingTimesheet != null && approvingIds.has(viewingTimesheet.id);
  const isViewingRejecting = viewingTimesheet != null && rejectingIds.has(viewingTimesheet.id);
  const isViewingProcessing = isViewingApproving || isViewingRejecting;

  // Days array driven by org settings + week start day (mirrors TimesheetGrid)
  const { days: DAYS, labels: DAY_LABELS } = useMemo(() => {
    const workingDays = orgSettings?.workingProfile?.workingDays
      ?? DEFAULT_TIMESHEET_SETTINGS.workingProfile.workingDays;
    return buildDaysFromStart(weekStartDay, workingDays);
  }, [weekStartDay, orgSettings?.workingProfile?.workingDays]);

  // ── Build flat rows for ApprovalGrid ────────────────────────────────────
  // Also enrich rows with delegation metadata so SegmentedApprovalsQueue can
  // split them and ApprovalGrid can show the "via delegation" label.
  const approvalRows: ApprovalRow[] = useMemo(() => {
    return filteredTimesheets.map(ts => {
      const employee = employees.find(e => e.id === ts.employeeId);
      const anomalyResult = timesheetAnomalies.get(ts.id);
      const policyViolations = timesheetPolicyViolations.get(ts.id) || [];
      const totalHours = calculateTimesheetTotal(ts);
      const daysSince = ts.submittedAt
        ? Math.floor((Date.now() - ts.submittedAt.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
      const isOverdue = daysSince > 7;
      const photoUrl =
        screenMode === 'live'
          ? getLivePhotoUrl(ts.employeePhotoUrl)
          : getPhotoUrl(ts.employeeName, ts.employeeId, employee?.photoUrl);

      // ── Delegation tagging (FE-14) ──────────────────────────────────────
      // A timesheet belongs to a delegated queue when the manager it was
      // originally assigned to has an active delegation to the current user.
      // ts.delegatedApproverId carries the original manager's ID after reassignment;
      // for non-reassigned timesheets it falls back to ts.approverId.
      // We match against activeDelegations using string comparison.
      const matchingDelegation = activeDelegations.find((d) => {
        const tsManagerId = ts.delegatedApproverId ?? ts.approverId;
        return tsManagerId !== undefined && String(tsManagerId) === String(d.managerId);
      });

      return {
        timesheet: ts,
        employeeName: ts.employeeName,
        department: employee?.department ?? '',
        weekEnding: ts.weekEnding,
        totalHours,
        policyViolations,
        anomalyResult,
        isOverdue,
        photoUrl,
        delegationManagerId: matchingDelegation?.managerId,
        delegationManagerName: matchingDelegation?.managerName,
      };
    });
  }, [filteredTimesheets, employees, timesheetAnomalies, timesheetPolicyViolations, screenMode, activeDelegations]);

  // Demo mode: when "Approving for" is set to another approver, swap in their mock queue.
  const displayRows: ApprovalRow[] =
    screenMode !== 'live' && approvingFor.type === 'other'
      ? (MOCK_APPROVER_ROWS[approvingFor.id] ?? [])
      : approvalRows;

  // Respect per-role visibility permissions — only include data the user is allowed to see.
  const viewingViolations = viewingTimesheet && canViewPolicyViolations()
    ? (timesheetPolicyViolations.get(viewingTimesheet.id) || [])
    : [];
  const viewingAnomalies = viewingTimesheet && canViewDetectedIssues()
    ? (timesheetAnomalies.get(viewingTimesheet.id)?.anomalies || [])
    : [];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <PageHeader
            title="Approvals"
            size={36}
            showBreadcrumb={false}
            icon={<ClipboardCheck />}
          />
          <div className="flex items-center gap-4 flex-wrap">
            {showApprovingFor && (
              <ApprovingForSwitcher
                currentUserName={currentEmployee.name}
                permittedApprovers={screenMode === 'live' ? approvableFor : MOCK_PERMITTED_APPROVERS}
                selected={approvingFor}
                onChange={setApprovingFor}
              />
            )}
            <FeatureFeedbackCollector feature="Approval Queue" />
          </div>
        </div>
      </div>

      {/* "Approving for" amber banner — full width, between header and KPI cards */}
      {showApprovingFor && (
        <ApprovingForBanner
          selected={approvingFor}
          onBackToMyself={() => setApprovingFor(myselfSelection)}
        />
      )}

      {/* KPI Summary Bar */}
      <ApprovalKpiCards
        summary={summary}
        anomalyFilter={anomalyFilter}
        setAnomalyFilter={setAnomalyFilter}
        setOverdueFilter={setOverdueFilter}
      />

      {/* Data Grid */}
      <ApprovalQueueCard
        isLoading={screenMode === 'live' && isLoadingPending}
        rows={displayRows}
        onSelectionChange={handleSelectionChange}
        clearSelection={clearSelection}
        onView={setViewingTimesheet}
        onApprove={handleGridApprove}
        onReject={setRejectingTimesheet}
        approvingIds={approvingIds}
        rejectingIds={rejectingIds}
        canApprove={canApprove}
        canReject={canReject}
        canBulkApprove={canBulkApprove}
        canViewPolicyViolations={canViewPolicyViolations}
        canViewDetectedIssues={canViewDetectedIssues}
        currentEmployeeId={currentEmployee.id}
        pendingCount={pendingTimesheets.length}
        overtimeEnabled={organizationSettings?.overtime?.enabled === true}
        overtimeTypes={apiActiveOvertimeTypes}
        departments={departments}
        departmentFilter={departmentFilter}
        setDepartmentFilter={setDepartmentFilter}
        anomalyFilter={anomalyFilter}
        setAnomalyFilter={setAnomalyFilter}
        overdueFilter={overdueFilter}
        setOverdueFilter={setOverdueFilter}
        overdueCount={summary.overdue}
        selectedIds={selectedIds}
        onBulkApprove={handleToolbarBulkApprove}
        onBulkReject={handleBulkRejectClick}
        isBulkProcessing={isBulkProcessing}
        isBulkApproving={isBulkApproving}
        isBulkRejecting={isBulkRejecting}
      />

      {/* View Timesheet Dialog */}
      <TimesheetReviewDialog
        timesheet={viewingTimesheet}
        onOpenChange={(open) => {
          if (!open) {
            setShowIssuesSheet(false);
            handleDialogCloseAttempt(() => setViewingTimesheet(null));
          }
        }}
        photoUrl={viewingTimesheet
          ? (screenMode === 'live'
            ? getLivePhotoUrl(viewingTimesheet.employeePhotoUrl)
            : getPhotoUrl(viewingTimesheet.employeeName, viewingTimesheet.employeeId, employees.find(e => e.id === viewingTimesheet.employeeId)?.photoUrl))
          : undefined}
        violations={viewingViolations}
        anomalies={viewingAnomalies}
        onShowIssues={() => setShowIssuesSheet(true)}
        isEditMode={isEditMode}
        canEdit={canEditTimesheetInApproval()}
        onStartEdit={() => {
          setIsEditMode(true);
          // In daily mode, auto-expand all entries so note inputs are immediately visible
          if (isDaily && viewingTimesheet) {
            const allIds = viewingTimesheet.entries
              .filter(e => DAYS.some(d => (e.hours[d as DayKey] ?? 0) !== 0))
              .map(e => e.id);
            setExpandedEntryIds(new Set(allIds));
          }
        }}
        tableProps={{
          days: DAYS,
          dayLabels: DAY_LABELS,
          isEditMode,
          isDaily,
          orgSettings,
          overtimeEnabled: organizationSettings?.overtime?.enabled === true,
          overtimeTypes: apiActiveOvertimeTypes,
          effectiveProjects,
          selectableProjects,
          costCentresByProjectCode,
          currentCompanyId: currentCompany?.id,
          screenMode,
          editedEntries,
          editingHours,
          setEditingHours,
          getHoursCellKey,
          expandedEntryIds,
          toggleEntryExpansion,
          shouldShowExpansionIcon,
          handleEntryFieldChange,
          handleEntryProjectChange,
          handleEntryRoleChange,
          handleEntryHoursChange,
          handleDailyNoteChange,
          onViewBookingDetails: setViewingBookingDetails,
          computedDailyTotals,
        }}
        footerProps={{
          currentEmployeeId: currentEmployee.id,
          isEditMode,
          isSavingEdits,
          hasUnsavedChanges,
          isViewingApproving,
          isViewingRejecting,
          isViewingProcessing,
          canApprove,
          canReject,
          onClose: () => handleDialogCloseAttempt(() => setViewingTimesheet(null)),
          onShowHistory: () => setShowAuditHistory(true),
          onCancelEdit: handleCancelEdit,
          onSaveEdits: handleSaveEdits,
          onApprove: () => {
            if (hasMissingCostCentres) {
              toast.error('Cannot approve timesheet', {
                description: 'All entries must have a cost centre assigned. Please add cost centres to all entries.',
                duration: 5000,
              });
              return;
            }
            if (viewingTimesheet) {
              handleApproveClick(viewingTimesheet, { fromViewDialog: true });
            }
          },
          onReject: () => {
            if (viewingTimesheet) {
              setRejectingTimesheet(viewingTimesheet);
              setViewingTimesheet(null);
            }
          },
        }}
      />

      {/* Issues Sheet — opens over the View Timesheet dialog */}
      {viewingTimesheet && (
        <IssuesSheet
          timesheet={viewingTimesheet}
          open={showIssuesSheet}
          onOpenChange={(open) => setShowIssuesSheet(open)}
          violations={viewingViolations}
          anomalies={viewingAnomalies}
          minSeverity={orgSettings.approvalScreen?.minSeverityToShow ?? 'all'}
          overtimeEnabled={organizationSettings?.overtime?.enabled === true}
        />
      )}

      {/* Reject Dialog */}
      <RejectTimesheetDialog
        timesheet={rejectingTimesheet}
        requiresReason={requiresRejectionReason()}
        isRejecting={isRejectingSingle}
        onCancel={() => setRejectingTimesheet(null)}
        onConfirm={rejectTimesheet}
      />

      {/* Post-approval rule confirmation (Accept / Decline) */}
      <PreStageRuleConfirmationDialog
        open={!!postApprovalConfirm}
        message={postApprovalConfirm?.message ?? ''}
        isSubmitting={isConfirmingApproval}
        onAccept={handlePostApprovalAccept}
        onDecline={handlePostApprovalDecline}
        description="A post-stage rule has generated the following message. Please review and choose whether to proceed with this approval."
        acceptLabel="Proceed"
        acceptingLabel="Approving..."
      />

      {/* F-02: Single-Item Approve Confirmation for Flagged Timesheets */}
      <FlaggedApprovalDialog
        timesheet={pendingFlaggedApproval}
        onOpenChange={(open) => { if (!open) setPendingFlaggedApproval(null); }}
        anomalyResult={pendingFlaggedApproval && canViewDetectedIssues()
          ? timesheetAnomalies.get(pendingFlaggedApproval.id)
          : undefined}
        policyViolations={pendingFlaggedApproval && canViewPolicyViolations()
          ? (timesheetPolicyViolations.get(pendingFlaggedApproval.id) || [])
          : []}
        approvalNotes={approvalNotes}
        onApprovalNotesChange={setApprovalNotes}
        onCancel={() => { setPendingFlaggedApproval(null); setApprovalNotes(''); }}
        onReview={() => {
          const ts = pendingFlaggedApproval;
          setPendingFlaggedApproval(null);
          setApprovalNotes('');
          if (ts) setViewingTimesheet(ts);
        }}
        onApproveAnyway={() => {
          const ts = pendingFlaggedApproval;
          const notes = approvalNotes;
          setPendingFlaggedApproval(null);
          setApprovalNotes('');
          if (ts) approveTimesheet(ts, notes);
        }}
      />

      {/* Bulk Approval Confirmation Dialog */}
      <BulkApprovalDialog
        open={showBulkConfirmDialog}
        onOpenChange={(open) => {
          if (isBulkApproving) return;
          setShowBulkConfirmDialog(open);
        }}
        selectedTimesheets={selectedTimesheets}
        selectedWithAnomalies={selectedWithAnomalies}
        timesheetAnomalies={timesheetAnomalies}
        bulkApprovalNotes={bulkApprovalNotes}
        onBulkApprovalNotesChange={setBulkApprovalNotes}
        isBulkApproving={isBulkApproving}
        onCancel={cancelBulkApproval}
        onConfirm={executeBulkApprove}
      />

      {/* F-12: Bulk Reject Confirmation Dialog */}
      <BulkRejectDialog
        open={showBulkRejectDialog}
        timesheets={selectedTimesheets}
        totalHours={selectedTimesheets.reduce((sum, ts) => sum + calculateTimesheetTotal(ts), 0)}
        requiresReason={requiresRejectionReason()}
        isRejecting={isBulkRejecting}
        onCancel={() => setShowBulkRejectDialog(false)}
        onConfirm={executeBulkReject}
      />

      {/* Audit History Dialog */}
      {viewingTimesheet?.id && (
        <AuditHistoryDialog
          timesheetId={viewingTimesheet.id}
          employeeName={viewingTimesheet.employeeName}
          weekEnding={viewingTimesheet.weekEnding}
          open={showAuditHistory}
          onOpenChange={setShowAuditHistory}
          timesheet={viewingTimesheet}
        />
      )}

      {/* Booking Details Modal (for variance review) */}
      {viewingBookingDetails && (
        <BookingDetailsModal
          open={!!viewingBookingDetails}
          onOpenChange={(open) => !open && setViewingBookingDetails(null)}
          booking={viewingBookingDetails}
          showVariance={true}
        />
      )}

      {/* Unsaved Changes Guard Dialog */}
      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        onOpenChange={setShowUnsavedChangesDialog}
        onKeepEditing={() => setShowUnsavedChangesDialog(false)}
        onDiscard={handleConfirmDiscard}
      />
    </div>
  );
}
